import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Droplets, Zap, Wind, Building2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { Section, AdSlot, SourceLink } from "@/components/common";
import { NewsCard } from "@/pages/Home";

const Val = ({ v }) => v ? <span className="text-white">{v}</span> :
  <span data-testid="not-reported" className="text-slate-500 italic">Not publicly reported</span>;

export default function StateDetail() {
  const { abbr } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setS(null); setErr(false);
    api.get(`/states/${abbr}`).then((r) => setS(r.data)).catch(() => setErr(true));
  }, [abbr]);

  if (err) return <Section className="py-20"><p className="text-slate-400">State not found. <button onClick={() => navigate("/states")} className="text-blue-400 underline">Back to explorer</button></p></Section>;
  if (!s) return <Section className="py-20"><p className="text-slate-400 font-mono">Loading…</p></Section>;

  const bd = s.facility_breakdown || {};
  const metrics = [
    { icon: Building2, label: "Facilities", val: s.facility_count, note: s.counting_method },
    { icon: Zap, label: "Power capacity", val: s.power_capacity_mw },
    { icon: Droplets, label: "Water use", val: s.water_use },
    { icon: Wind, label: "Cooling systems", val: s.cooling_systems },
  ];

  return (
    <Section className="py-12 sm:py-16">
      <button onClick={() => navigate("/states")} data-testid="back-to-states" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-6"><ArrowLeft size={15} /> All states</button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
        <div>
          <p className="eyebrow mb-1">{s.region} · {s.abbr}</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-white">{s.name}</h1>
        </div>
        <ConfidenceBadge level={s.data_confidence} />
      </div>

      <p className="text-slate-300 max-w-3xl mt-4 leading-relaxed">{s.summary}</p>

      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4 mt-6 flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-amber-200/90 text-sm">Counts may differ depending on whether a source counts individual buildings, campuses, or companies. Proposed projects are never combined with operating facilities. We never invent or estimate totals without a cited methodology.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        {metrics.map((m) => (
          <div key={m.label} data-testid={`state-metric-${m.label}`} className="card p-6">
            <div className="flex items-center gap-2 mb-2"><m.icon size={16} className="text-[#3B82F6]" /><p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{m.label}</p></div>
            <p className="text-lg"><Val v={m.val} /></p>
            {m.note && <p className="text-xs text-slate-500 mt-2">Counting method: {m.note}</p>}
          </div>
        ))}
      </div>

      {s.facility_breakdown && (
        <div className="card p-6 mt-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400 mb-4">Facility status (kept separate — proposed is not added to operating)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[["operating", "Operating"], ["construction", "Under construction"], ["proposed", "Proposed"], ["closed", "Closed / canceled"]].map(([k, l]) => (
              <div key={k} className="bg-[#0B0F17] rounded-lg p-4 border border-white/5">
                <p className="text-xs text-slate-500 mb-1">{l}</p>
                <p className="text-sm"><Val v={bd[k]} /></p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(s.sources || []).length > 0 && (
        <div className="card p-6 mt-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400 mb-3">Sources</p>
          <div className="space-y-2">{s.sources.map((src, i) => (
            <div key={i} className="flex items-center gap-2"><ConfidenceBadge level={src.confidence || "reported"} /><SourceLink source={src} /></div>
          ))}</div>
          <p className="text-xs text-slate-500 mt-4 font-mono">Last updated: {new Date(s.updated_at).toLocaleDateString()}</p>
        </div>
      )}

      <div className="my-8"><AdSlot label="Sponsored · State page" testid="state-ad-slot" /></div>

      <h2 className="font-display font-bold text-2xl text-white mb-4">Recent news for {s.name}</h2>
      <div className="space-y-4">
        {(s.recent_news || []).length === 0 && <p className="text-slate-400 text-sm">No state-tagged news yet.</p>}
        {(s.recent_news || []).map((n) => <NewsCard key={n.id} n={n} />)}
      </div>
    </Section>
  );
}
